import React from "react";
import "./src/styles/global.css";
import midnightRefresh from "./src/utils/midnightRefresh";
import { getBuildWebiste } from "./src/utils/env";
import { buildI18nextBrowserProvider, initBrowserI18n, getLocaleInstanceSync } from "./src/i18next";
import { currentLanguage } from "./src/utils/routes";
import { formatLocale } from "./src/i18next/utils";
import { getNamespaceForBuildType } from "./src/i18next/resources";
import type { GatsbyBrowser } from "gatsby";
import type { i18n as I18nInstance } from "i18next";
import { EmotionCacheProvider } from "./src/emotion/EmotionCacheProvider";

interface PageContext {
  localeStrings: {
    currentLocale: string;
    buildType: string;
  };
}

declare global {
  interface Window {
    i18n: I18nInstance;
    __GATSBY_I18N_INIT__: Promise<I18nInstance> | null;
    __FLEXSEARCH__: any;
    start?: Date;
    _paq?: any[];
  }
  const __PATH_PREFIX__: string;
  const FlexSearch: any;
}

/**
 * Implement Gatsby's Browser APIs in this file.
 *
 * See: https://www.gatsbyjs.org/docs/browser-apis/
 */
(global as any).FlexSearch = require("flexsearch");

export const onClientEntry = async () => {
  // Initialize i18n before hydration starts for React 18 compatibility
  const currentLocale = currentLanguage();
  const lang = formatLocale(currentLocale || "en");

  // Convert buildType to namespace to match what gatsby-node passes to pageContext
  // e.g., "mobile" -> "mobile_web", "www" -> "website2.website"
  const buildType = getBuildWebiste();
  const namespace = getNamespaceForBuildType(buildType);

  window.__GATSBY_I18N_INIT__ = initBrowserI18n(lang, namespace);
  await window.__GATSBY_I18N_INIT__;

  // Pre-create the locale instance that will be used for hydration
  getLocaleInstanceSync(lang);

  // IntersectionObserver polyfill for gatsby-background-image (Safari, IE)
  if (typeof window.IntersectionObserver === `undefined`) {
    import(`intersection-observer`);
  }

  if (process.env.GATSBY_BUILD_WEBSITE && process.env.GATSBY_BUILD_WEBSITE.indexOf("blog") > -1) {
    fetch(`${__PATH_PREFIX__}/flexsearch_index.json`)
      .then(function (response) {
        return response.json();
      })
      .then(function (index) {
        Object.keys(index).forEach((lng) => {
          Object.keys(index[lng].index).forEach((idx) => {
            const index_ = index[lng].index[idx];

            if (index_.attrs.stemmer !== undefined || index_.attrs.filter !== undefined) {
              try {
                require("./src/creators/flexsearch-en");
              } catch (e) {
                // Silently fail if language file not found
              }
            }
            const indexObj = new FlexSearch(index_.attrs);
            indexObj.import(index_.values);
            index_.values = indexObj;
          });
        });
        window.__FLEXSEARCH__ = index;
      })
      .catch(() => {
        // Failed to load search index
      });
  }

  midnightRefresh();
};

function getDuration() {
  const start = window.start || new Date();
  const now = new Date();
  const difference = now.getTime() - start.getTime();

  if (difference === 0) {
    return null;
  }

  return difference;
}

let first = true;
export const onRouteUpdate = ({ location, prevLocation }, pluginOptions) => {
  if (!window._paq) return;

  const { _paq } = window;
  const url = location && location.pathname + location.search + location.hash;
  const prevUrl = prevLocation && prevLocation.pathname + prevLocation.search + prevLocation.hash;

  // document.title workaround stolen from:
  // https://github.com/gatsbyjs/gatsby/blob/master/packages/gatsby-plugin-google-analytics/src/gatsby-browser.js
  const sendPageView = () => {
    const { title } = document;

    prevUrl && _paq.push(["setReferrerUrl", prevUrl]);
    _paq.push(["setCustomUrl", url]);
    _paq.push(["setDocumentTitle", title]);
    _paq.push(["trackPageView"]);
    _paq.push(["enableLinkTracking"]);
    _paq.push(["trackAllContentImpressions"]);
  };

  if (first) {
    first = false;
    _paq.push(["trackEvent", "javascript", "load", "duration", getDuration()]);
  }

  // Minimum delay for reactHelmet's requestAnimationFrame
  setTimeout(sendPageView, 32);

  return null;
};

export const wrapRootElement: GatsbyBrowser["wrapRootElement"] = ({ element }) => {
  // Wrap with emotion cache provider to ensure consistent styling between server and client
  return <EmotionCacheProvider>{element}</EmotionCacheProvider>;
};

export const wrapPageElement: GatsbyBrowser<object, PageContext>["wrapPageElement"] = ({ element, props }) => {
  const { currentLocale, buildType } = props.pageContext.localeStrings || {};

  if (!currentLocale) {
    return element;
  }

  // Use buildType from pageContext (which is actually the namespace like "mobile_web")
  // Fallback to converting env var to namespace if not provided
  const namespace = buildType || getNamespaceForBuildType(getBuildWebiste());

  const I18nextBrowserProvider = buildI18nextBrowserProvider(namespace);

  return <I18nextBrowserProvider locale={currentLocale}>{element}</I18nextBrowserProvider>;
};
